<template>
  <div class="users-page">
    <h2>User Management</h2>
    
    <div class="tabs">
      <button 
        :class="['tab', { active: activeTab === 'all' }]" 
        @click="activeTab = 'all'"
      >
        All Users
      </button>
      <button 
        :class="['tab', { active: activeTab === 'admins' }]" 
        @click="activeTab = 'admins'"
      >
        Admins
      </button>
    </div>

    <div class="toolbar">
      <input v-model="search" placeholder="Search by email or name" />
      <button @click="fetchUsers" :disabled="loading">Refresh</button>
    </div>

    <div v-if="loading" class="loading">Loading users...</div>
    
    <div v-else class="content">
      <!-- All Users Tab -->
      <div v-if="activeTab === 'all'">
        <table class="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>UID</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in filteredUsers" :key="user.uuid">
              <td>{{ user.nom || 'N/A' }}</td>
              <td>{{ user.mail }}</td>
              <td class="uid-cell">
                <code>{{ user.uuid }}</code>
                <button class="copy-btn" @click="copyToClipboard(user.uuid)" title="Copy UID">
                  📋
                </button>
              </td>
              <td>
                <span v-if="isUserAdmin(user.uuid)" class="chip admin-chip">Admin</span>
                <span v-else class="chip user-chip">User</span>
              </td>
              <td>
                <button 
                  v-if="!isUserAdmin(user.uuid)" 
                  class="btn-promote" 
                  @click="showPromoteModal(user)"
                >
                  Make Admin
                </button>
                <button 
                  v-else-if="user.uuid !== currentUserUid" 
                  class="btn-demote" 
                  @click="showDemoteModal(user)"
                >
                  Remove Admin
                </button>
                <span v-else class="self-label">You</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!filteredUsers.length" class="empty">No users found</div>
      </div>

      <!-- Admins Tab -->
      <div v-if="activeTab === 'admins'">
        <div class="admin-list">
          <div class="admin-card" v-for="admin in adminUsers" :key="admin.uuid">
            <div class="admin-info">
              <h3>{{ admin.nom || 'Admin User' }}</h3>
              <p class="email">{{ admin.mail }}</p>
              <div class="uid-display">
                <code>{{ admin.uuid }}</code>
                <button class="copy-btn-small" @click="copyToClipboard(admin.uuid)">📋</button>
              </div>
            </div>
            <div class="admin-actions">
              <span v-if="admin.uuid === currentUserUid" class="self-badge">You</span>
              <button 
                v-else 
                class="btn-remove" 
                @click="showDemoteModal(admin)"
              >
                Remove Admin
              </button>
            </div>
          </div>
        </div>
        <div v-if="!adminUsers.length" class="empty">No admin users</div>
      </div>
    </div>

    <!-- Promote Confirmation Modal -->
    <div v-if="showPromoteConfirm" class="modal-overlay" @click.self="showPromoteConfirm = false">
      <div class="modal-container">
        <div class="modal-header">
          <h3>Promote to Admin</h3>
          <button class="close-btn" @click="showPromoteConfirm = false">×</button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to grant admin privileges to:</p>
          <div class="user-highlight">
            <strong>{{ selectedUser?.nom || 'User' }}</strong>
            <br>
            <span>{{ selectedUser?.mail }}</span>
          </div>
          <p class="warning">⚠️ This will give them full access to the admin panel.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="showPromoteConfirm = false">Cancel</button>
          <button class="btn-confirm" @click="promoteUser">Confirm</button>
        </div>
      </div>
    </div>

    <!-- Demote Confirmation Modal -->
    <div v-if="showDemoteConfirm" class="modal-overlay" @click.self="showDemoteConfirm = false">
      <div class="modal-container">
        <div class="modal-header">
          <h3>Remove Admin Access</h3>
          <button class="close-btn" @click="showDemoteConfirm = false">×</button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to remove admin privileges from:</p>
          <div class="user-highlight">
            <strong>{{ selectedUser?.nom || 'User' }}</strong>
            <br>
            <span>{{ selectedUser?.mail }}</span>
          </div>
          <p class="warning">⚠️ They will lose access to the admin panel.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="showDemoteConfirm = false">Cancel</button>
          <button class="btn-confirm-danger" @click="demoteUser">Remove Access</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db, auth } from '@/firebase/init';
import { collection, getDocs, doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';

export default {
  name: 'UsersPage',
  data() {
    return {
      loading: false,
      users: [],
      adminUids: new Set(),
      search: '',
      activeTab: 'all',
      showPromoteConfirm: false,
      showDemoteConfirm: false,
      selectedUser: null,
      currentUserUid: null,
    };
  },
  computed: {
    filteredUsers() {
      const s = (this.search || '').toLowerCase();
      if (!s) return this.users;
      return this.users.filter(u =>
        (u.mail || '').toLowerCase().includes(s) ||
        (u.nom || '').toLowerCase().includes(s) ||
        (u.uuid || '').toLowerCase().includes(s)
      );
    },
    adminUsers() {
      return this.users.filter(u => this.isUserAdmin(u.uuid));
    }
  },
  methods: {
    async fetchUsers() {
      this.loading = true;
      try {
        const usersRef = collection(db, 'users');
        const snap = await getDocs(usersRef);
        this.users = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // Fetch admin list from Firestore
        await this.fetchAdminList();
      } catch (e) {
        console.error('Failed to load users', e);
      } finally {
        this.loading = false;
      }
    },
    async fetchAdminList() {
      try {
        const adminRef = doc(db, 'admin', 'admins');
        const snap = await getDoc(adminRef);
        if (snap.exists()) {
          const data = snap.data();
          this.adminUids = new Set(data.uids || []);
        } else {
          // Initialize with only the specified admin UID
          console.log('Initializing admin collection with single admin UID');
          this.adminUids = new Set(['6KAyZ5CrB3cNJzQdbrgRIUy57EV2']);
          await this.saveAdminList();
        }
      } catch (e) {
        console.error('Failed to load admin list', e);
      }
    },
    async saveAdminList() {
      try {
        const adminRef = doc(db, 'admin', 'admins');
        await setDoc(adminRef, {
          uids: Array.from(this.adminUids),
          updatedAt: Date.now()
        });
      } catch (e) {
        console.error('Failed to save admin list', e);
        throw e;
      }
    },
    isUserAdmin(uid) {
      return this.adminUids.has(uid);
    },
    showPromoteModal(user) {
      this.selectedUser = user;
      this.showPromoteConfirm = true;
    },
    showDemoteModal(user) {
      this.selectedUser = user;
      this.showDemoteConfirm = true;
    },
    async promoteUser() {
      try {
        if (!this.selectedUser) return;
        this.adminUids.add(this.selectedUser.uuid);
        await this.saveAdminList();
        this.showPromoteConfirm = false;
        this.selectedUser = null;
        alert('User promoted to admin successfully!');
      } catch (e) {
        console.error('Failed to promote user', e);
        alert('Failed to promote user. Please try again.');
      }
    },
    async demoteUser() {
      try {
        if (!this.selectedUser) return;
        this.adminUids.delete(this.selectedUser.uuid);
        await this.saveAdminList();
        this.showDemoteConfirm = false;
        this.selectedUser = null;
        alert('Admin access removed successfully!');
      } catch (e) {
        console.error('Failed to demote user', e);
        alert('Failed to remove admin access. Please try again.');
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        alert('UID copied to clipboard!');
      }).catch(err => {
        console.error('Failed to copy', err);
      });
    }
  },
  mounted() {
    this.currentUserUid = auth.currentUser?.uid;
    this.fetchUsers();
  }
};
</script>

<style scoped>
.users-page {
  padding: 20px;
}

h2 {
  color: #e37267;
  margin-bottom: 20px;
}

.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e6d1c7;
}

.tab {
  padding: 12px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: #666;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: -2px;
}

.tab:hover {
  color: #e37267;
}

.tab.active {
  color: #e37267;
  border-bottom-color: #e37267;
}

.toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.toolbar input {
  flex: 1;
  max-width: 400px;
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
}

.toolbar button {
  padding: 10px 20px;
  background: #e37267;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.toolbar button:hover {
  background: #d66259;
}

.toolbar button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading {
  padding: 40px;
  text-align: center;
  color: #999;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.users-table th {
  background: #fffef9;
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #666;
  border-bottom: 2px solid #e6d1c7;
}

.users-table td {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.users-table tbody tr:hover {
  background: #fafafa;
}

.uid-cell {
  font-family: monospace;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.uid-cell code {
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.copy-btn, .copy-btn-small {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.copy-btn:hover, .copy-btn-small:hover {
  opacity: 1;
}

.chip {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.admin-chip {
  background: #fef3c7;
  color: #f59e0b;
}

.user-chip {
  background: #e0e7ff;
  color: #6366f1;
}

.btn-promote {
  padding: 6px 16px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}

.btn-promote:hover {
  background: #059669;
}

.btn-demote {
  padding: 6px 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}

.btn-demote:hover {
  background: #dc2626;
}

.self-label {
  color: #999;
  font-style: italic;
  font-size: 13px;
}

.empty {
  padding: 40px;
  text-align: center;
  color: #999;
  background: white;
  border-radius: 8px;
}

/* Admin Cards */
.admin-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.admin-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fffef9 100%);
  border: 2px solid #f59e0b;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.admin-info h3 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 18px;
}

.admin-info .email {
  margin: 0 0 12px 0;
  color: #666;
  font-size: 14px;
}

.uid-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.uid-display code {
  background: rgba(255,255,255,0.6);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.self-badge {
  background: #e37267;
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.btn-remove {
  padding: 8px 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.btn-remove:hover {
  background: #dc2626;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: grid;
  place-items: center;
  z-index: 9999;
}

.modal-container {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 2px solid #e37267;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fffef9;
}

.modal-header h3 {
  margin: 0;
  color: #e37267;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 32px;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #e37267;
}

.modal-body {
  padding: 24px;
}

.user-highlight {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
  border-left: 4px solid #e37267;
}

.warning {
  color: #f59e0b;
  font-size: 14px;
  margin-top: 16px;
}

.modal-footer {
  padding: 16px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  background: #fafafa;
}

.btn-cancel {
  padding: 10px 20px;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.btn-cancel:hover {
  background: #e5e5e5;
}

.btn-confirm {
  padding: 10px 20px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.btn-confirm:hover {
  background: #059669;
}

.btn-confirm-danger {
  padding: 10px 20px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.btn-confirm-danger:hover {
  background: #dc2626;
}
</style>
